import { findCategoryItem } from "@/components/edition/category/utils/categoryTree";
import { useCallback, useMemo } from "react";
import { checkIsLastDepth, convertCategoriesToLnbItems, findCategoryPath, findDepthItemByCategoryId } from "../utils/categoryMenu";
import { getSubjectNameByParentId, SUBJECT_PARENT_ID, useCategoryMenu } from "./useCategoryMenu";
const SUBJECT_IDS = Object.values(SUBJECT_PARENT_ID) // [10, 11, 12, 13, 14]
;
/**
 * 시스템 카테고리 데이터를 조회하고 파생값을 계산하는 훅
 * - 카테고리 메뉴 API
 * - 과목 추출, LNB 데이터, 카테고리 계층 구조, 브레드크럼 등
 */ export const useSystemCategoryData = (selectedCategoryId)=>{
    // 카테고리 ID에서 과목 ID 추출 (첫 2자리)
    const selectedSubject = useMemo(()=>{
        if (!selectedCategoryId) return SUBJECT_PARENT_ID.영어;
        const firstTwoDigits = selectedCategoryId.slice(0, 2);
        const subjectId = parseInt(firstTwoDigits, 10);
        return SUBJECT_IDS.includes(subjectId) ? subjectId : SUBJECT_PARENT_ID.영어;
    }, [
        selectedCategoryId
    ]);
    // 현재 페이지가 과목 페이지인지 확인 (id가 10, 11, 12, 13, 14 중 하나)
    const isSubjectPage = useMemo(()=>{
        if (!selectedCategoryId) return false;
        const idAsNumber = parseInt(selectedCategoryId, 10);
        return SUBJECT_IDS.includes(idAsNumber);
    }, [
        selectedCategoryId
    ]);
    // API 호출
    const { data: categoryMenuData , isLoading: isMenuLoading  } = useCategoryMenu(selectedSubject);
    // 현재 선택된 카테고리가 속한 depth 아이템 찾기
    const currentDepthItem = useMemo(()=>{
        return findDepthItemByCategoryId((categoryMenuData === null || categoryMenuData === void 0 ? void 0 : categoryMenuData.depthItems) || [], selectedCategoryId);
    }, [
        categoryMenuData === null || categoryMenuData === void 0 ? void 0 : categoryMenuData.depthItems,
        selectedCategoryId
    ]);
    // LNB 데이터 변환
    const lnbItems = useMemo(()=>{
        return convertCategoriesToLnbItems((categoryMenuData === null || categoryMenuData === void 0 ? void 0 : categoryMenuData.depthItems) || []);
    }, [
        categoryMenuData === null || categoryMenuData === void 0 ? void 0 : categoryMenuData.depthItems
    ]);
    // LNB 제목: 과목명
    const lnbTitle = getSubjectNameByParentId(selectedSubject) || "";
    // 카테고리 계층 구조
    const categoryPath = useMemo(()=>findCategoryPath(currentDepthItem, selectedCategoryId), [
        currentDepthItem,
        selectedCategoryId
    ]);
    const isLastDepth = useMemo(()=>checkIsLastDepth(currentDepthItem, selectedCategoryId), [
        currentDepthItem,
        selectedCategoryId
    ]);
    // 선택된 카테고리의 직계 자식 카테고리 목록
    const directChildCategories = useMemo(()=>{
        if (!selectedCategoryId) return [];
        const item = findCategoryItem(selectedCategoryId, lnbItems);
        return (item === null || item === void 0 ? void 0 : item.items) || [];
    }, [
        selectedCategoryId,
        lnbItems
    ]);
    const currentTitle = categoryPath.length > 0 ? categoryPath[categoryPath.length - 1].title : lnbTitle;
    // LNB에서 사용할 href 생성 함수
    const getCategoryHref = useCallback((item)=>{
        if (item.link) return item.link;
        return "/categories?id=".concat(item.categoryId);
    }, []);
    // BreadCrumb items
    const breadcrumbItems = useMemo(()=>[
            {
                label: lnbTitle
            },
            ...categoryPath.map((item, index)=>({
                    label: item.title,
                    ...index < categoryPath.length - 1 && item.link ? {
                        href: item.link
                    } : {}
                }))
        ], [
        lnbTitle,
        categoryPath
    ]);
    // LNB 헤더 과목 목록
    const depthCategories = useMemo(()=>Object.entries(SUBJECT_PARENT_ID).map((param)=>{
            let [name, id] = param;
            return {
                categoryId: String(id),
                title: name
            };
        }), []);
    return {
        // API 상태
        isMenuLoading,
        selectedSubject,
        // 파생 데이터
        isSubjectPage,
        lnbItems,
        lnbTitle,
        categoryPath,
        isLastDepth,
        directChildCategories,
        currentTitle,
        breadcrumbItems,
        depthCategories,
        getCategoryHref
    };
};
